import React, {useState} from 'react';
import {withTranslation} from 'react-i18next';
import {useEntityActions} from '../../ducks/entityactions';
import {useTypeDetail} from '../../ducks/typedetail-flattened';
import {useDetailFormattersByType, useIndexedFormatters} from '../FormattersContext';
import {Tab, Card, InternalNavLink, ModalDialog} from '../../ui-toolkit';
import {determineAvailableEntityEditors} from '../../ducks/formatterhelpers';
import {useEntityDetail} from '../../ducks/entitydetail';

import Alert from '../../shared-components/elements/alert/Alert';
import LoadingState from '../../shared-components/custom-features/loading-state/LoadingState';

import _ from 'lodash';

function EntityPeekModal({entityType, entity, hide, t, entityLink}) {
    return (
        <ModalDialog title={entityType.dataTypeName + t('entityDetailPage.title')} show={true} onHide={hide} size="xl">
            <ModalDialog.Body>
                <PeekModalBody
                    entityType={entityType}
                    entityId={entity.entityId}
                    hide={hide}
                    t={t}
                    entityLink={entityLink}
                />
            </ModalDialog.Body>
        </ModalDialog>
    );
}

function PeekModalBody({entityType, entityId, t, entityLink}) {
    const [chosenFormatter, setChosenFormatter] = useState(null);
    const entityActions = useEntityActions(entityType.dataTypeId, entityId);
    const formatters = useDetailFormattersByType(entityType.dataTypeId);

    // ensure that the full type (with descendents) of the one we are peeking is loaded
    const typeDetail = useTypeDetail(entityType.dataTypeId);

    if (entityActions.loading || !typeDetail) {
        return <p>{t('generalShared.loading')}</p>;
    }

    const editors = determineAvailableEntityEditors(formatters, 'detail', chosenFormatter, entityActions);
    if (editors === null) {
        return <Alert>{t('entityDetailPage.noFormatters')}</Alert>;
    }

    if (editors.editor != null) {
        return (
            <PeekModalContent
                cellFormatter={editors.editor}
                entityType={entityType}
                entityLink={entityLink}
                entityId={entityId}
                t={t}
            />
        );
    } else {
        return (
            <>
                {editors.allEditors.length > 1 && (
                    <EditorSelection
                        allEditors={editors.allEditors}
                        t={t}
                        setChosenFormatter={setChosenFormatter}
                        chosenFormatter={chosenFormatter}
                    />
                )}
            </>
        );
    }
}

// there are multiple suitable detail formatters for this entity, so the user will have to choose one
// This is basically a misconfiguration of the formatters, but there is no way for us to solve this, so we will just have to allow the user to make a decision
function EditorSelection({allEditors, chosenFormatter, setChosenFormatter}) {
    return (
        <Tab>
            {_.map(allEditors, (ed) => (
                <Tab.Option onClick={() => setChosenFormatter(ed.id)} key={ed.id} active={chosenFormatter === ed.id}>
                    {ed.menuLabel ? ed.menuLabel : ed.name}
                </Tab.Option>
            ))}
        </Tab>
    );
}

// the detail page has been selected (or there is only a single one), so display the entity
function PeekModalContent({cellFormatter, entityType, entityLink, entityId, t}) {
    const indexedFormatters = useIndexedFormatters();
    const projectionStr = cellFormatter
        .projectionFunction(cellFormatter.settings, entityType, indexedFormatters)
        ?.join(', ');

    const {entityDetail, loading} = useEntityDetail(entityType.dataTypeId, entityId, null, projectionStr);

    if (loading != 0) {
        return <LoadingState />;
    }

    function Detail(props) {
        let DetailFormatter = cellFormatter;

        return <DetailFormatter.Formatter {...props} />;
    }

    return (
        <>
            {entityLink && (
                <InternalNavLink color="primary" link to={entityLink}>
                    {t('nav.viewDetailPage')}
                </InternalNavLink>
            )}

            {cellFormatter == null && (
                <Card>
                    {t('entityDetailPage.error')}
                    <pre>{JSON.stringify(entityDetail, null, 4)}</pre>
                </Card>
            )}

            {cellFormatter != null && (
                <Detail isRoot={true} entityType={entityType} formatter={cellFormatter} entity={entityDetail} />
            )}
        </>
    );
}

export default withTranslation()(EntityPeekModal);
